// src/features/rooms/thunks/thunkPlaceDevicesFromStandardRooms.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { AppDispatch, IRootState } from "../../../app/store";
import type { IMcBimItem, IRoomSummary } from "@backend/src/shared/schemas/schemas";

import { selectCompCodePath } from "../../../globalSelectors";

import { openapi } from "../../../../openapi";
import { selectStandardRoomPlacementStatsInSelectedRooms } from "../../../Room/roomSelectors";
import { PartialMcBimSpace } from "../../../utils";
import {
  thunkToast,
  addNonPublischedDevicesFromAlgosInRedux,
  getHeightFromFirstCeilingGrid,
} from "../../designSlice";
import { convertMcBimItemToDevice } from "../deviceUtils";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import { copyDevicesFromASpaceToAnotherSpace } from "@multiconsult-group/multiplacer-core";
import { selectHttpGetRoomsCurrentFloorResult } from "../../../Room/httpRoomSelectors";
import { selectHttpMcBimSpaceItems } from "../../../McBim/httpMcBimSelectors";

const selectProjectNo = (state: IRootState) => state.design.projectNo;
type Result = {
  targetRoomsCapped: number;
  uniqueStandardRooms: number;

  totalItemsFound: number;
  undertakDevicesFound: number;

  copiedDevicesCount: number;

  failedTargetRoomsCount: number;
  failedTargetRooms: string[];

  devicesToAdd: IDevice[];
};

type Args = {
  cap?: number; // default 100
  addToNonPublished?: boolean; // default true
};

export const thunkPlaceDevicesFromStandardRooms = createAsyncThunk<
  Result,
  Args | void,
  { state: IRootState; dispatch: AppDispatch }
>("rooms/placeDevicesFromStandardRooms", async (args, { getState, dispatch, rejectWithValue }) => {
  const state = getState();

  const cap = args?.cap ?? 100;
  const addToNonPublished = args?.addToNonPublished ?? true;

  const projectNo = selectProjectNo(state);
  const compCodePath = selectCompCodePath(state);

  if (!compCodePath) {
    dispatch(
      thunkToast({
        header: "Feil",
        text: "Mangler søkesti til Artikkelnr – kan ikke identifisere undertakskomponenter.",
        isSuccess: false,
      })
    );
    return rejectWithValue("Missing compCodePath");
  }

  const stats = selectStandardRoomPlacementStatsInSelectedRooms(state);

  const targetRoomNumbers = (stats.placeableRoomNumbers ?? []).slice(0, cap);
  if (targetRoomNumbers.length === 0) {
    return {
      targetRoomsCapped: 0,
      uniqueStandardRooms: 0,
      totalItemsFound: 0,
      undertakDevicesFound: 0,
      copiedDevicesCount: 0,
      failedTargetRoomsCount: 0,
      failedTargetRooms: [],
      devicesToAdd: [],
    };
  }

  // room summaries (for copy fn)
  const roomsMap = selectHttpGetRoomsCurrentFloorResult(state);
  const mcbimSpaces = selectHttpMcBimSpaceItems(state);

  const spaceByName = new Map<string, any>();
  for (const s of mcbimSpaces ?? []) spaceByName.set(s.name, s);

  // Build mapping target -> standardRoomName
  const roomToStd = new Map<string, string>();
  for (const r of stats.roomsWithStandardRoom ?? []) {
    if (r?.roomNumber && r?.rfpMalRom) roomToStd.set(r.roomNumber, r.rfpMalRom);
  }

  // Group targets by standard room
  const stdToTargets = new Map<string, string[]>();
  for (const targetRoomNo of targetRoomNumbers) {
    const std = roomToStd.get(targetRoomNo);
    if (!std) continue;
    const arr = stdToTargets.get(std) ?? [];
    arr.push(targetRoomNo);
    stdToTargets.set(std, arr);
  }

  const uniqueStandardRooms = Array.from(stdToTargets.keys());

  // Fetch per standard room (items + space geometry)
  let totalItemsFound = 0;
  let undertakDevicesFound = 0;

  const devicesByStandardRoom = new Map<string, IDevice[]>(); // devices "in std space coordinates"

  for (const stdRoomName of uniqueStandardRooms) {
    // 1) items in standard room
    const items = await dispatch(
      openapi.endpoints.getMcbimItemsInSpace.initiate({
        projectNo,
        spaceName: stdRoomName,
      })
    )
      .unwrap()
      .catch(() => [] as IMcBimItem[]);

    totalItemsFound += items.length;

    const mcbimSpace = mcbimSpaces?.find((s) => s.name === stdRoomName);
    const ceilingHeight = getHeightFromFirstCeilingGrid(mcbimSpace?.ceilingGrids ?? []);

    // 2) convert -> devices (filter undertak)
    const devicesToCopyFromStd = items.reduce((acc: IDevice[], item) => {
      const d = convertMcBimItemToDevice({
        mcBimItem: item,
        roomNumber: stdRoomName,
        roomName: mcbimSpace?.longName,
        compCodePath,
        projectNo,
        ceilingHeight: ceilingHeight ?? 2700,
      });
      if (d) {
        undertakDevicesFound += 1;
        acc.push(d);
      }
      return acc;
    }, []);

    devicesByStandardRoom.set(stdRoomName, devicesToCopyFromStd);

    // 3) ensure standard space exists in RTK cache (always initiate; RTK caches)
    await dispatch(
      openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
        projectNo,
        name: stdRoomName,
      })
    )
      .unwrap()
      .catch(() => null);
  }

  // Now place/copy to targets
  const failedTargetRooms: string[] = [];
  const devicesToAdd: IDevice[] = [];

  for (const [stdRoomName, targets] of stdToTargets.entries()) {
    const devicesToCopyFromStd = devicesByStandardRoom.get(stdRoomName) ?? [];
    if (devicesToCopyFromStd.length === 0) continue;

    const standardSpace = await dispatch(
      openapi.endpoints.getMcbimSingleMcbimSpace.initiate({
        projectNo,
        name: stdRoomName,
      })
    )
      .unwrap()
      .catch(() => null);

    if (!standardSpace?.polygon) {
      // If no geometry, all targets under this standard fail
      failedTargetRooms.push(...targets);
      continue;
    }

    for (const targetRoomNo of targets) {
      const targetRoom = roomsMap[targetRoomNo] as IRoomSummary | undefined;
      const targetSpace = spaceByName.get(targetRoomNo);

      if (!targetRoom || !targetSpace?.polygon) {
        failedTargetRooms.push(targetRoomNo);
        continue;
      }

      // ✅ copy kan kaste exception iblant – håndter det her
      let copied: IDevice[] | null | undefined = undefined;
      try {
        copied = copyDevicesFromASpaceToAnotherSpace({
          devicesToCopy: devicesToCopyFromStd,
          spaceToCopyFrom: standardSpace as PartialMcBimSpace,
          spaceToCopyTo: targetSpace as PartialMcBimSpace,
          roomToCopyTo: targetRoom as IRoomSummary,
        });
      } catch (err) {
        console.warn("[placeFromStandardRooms] copyDevicesFromASpaceToAnotherSpace feilet", {
          stdRoomName,
          targetRoomNo,
          err,
        });
        failedTargetRooms.push(targetRoomNo); // ✅ tell opp feilrom når copy kaster
        continue;
      }

      if (copied == null || copied.length === 0) {
        failedTargetRooms.push(targetRoomNo);
        continue;
      }

      const fixed = copied.map((d) => ({
        ...d,
        roomNumber: targetRoomNo,
        roomName: targetSpace.longName ?? targetRoom?.roomName ?? d.roomName,
        floor: targetSpace.buildingStory?.name ?? d.floor ?? "",
        projectNo: String(projectNo),
      }));

      devicesToAdd.push(...fixed);
    }
  }

  if (addToNonPublished && devicesToAdd.length > 0) {
    dispatch(addNonPublischedDevicesFromAlgosInRedux(devicesToAdd));
  }

  const result: Result = {
    targetRoomsCapped: targetRoomNumbers.length,
    uniqueStandardRooms: uniqueStandardRooms.length,

    totalItemsFound,
    undertakDevicesFound,

    copiedDevicesCount: devicesToAdd.length,

    failedTargetRoomsCount: failedTargetRooms.length,
    failedTargetRooms,

    devicesToAdd,
  };

  return result;
});
